import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { BookOpen, GraduationCap, Sparkles } from "lucide-react";

const COPY = {
  pt: {
    seoH2: "Conteúdo e metodologia · aulas de francês",
    title: "Quem eu sou, o que ensino e como.",
    blocks: [
      {
        icon: "who",
        kicker: "Quem eu sou",
        text:
          "Professor francês nativo de Paris, Lionel oferece curso de francês online personalizado e aulas online de francês para todos os níveis do A1 ao C2, com foco em conversação, gramática, vocabulário e preparação para os exames DELF, DALF, TEF e TCF.",
      },
      {
        icon: "plans",
        kicker: "Planos de curso",
        list: [
          {
            head: "Curso Online Completo · A1 a C2",
            body: "Trilha estruturada do iniciante ao avançado, com materiais exclusivos.",
          },
          {
            head: "Curso Intensivo · 2× por semana",
            body: "Aceleração com feedback personalizado em cada aula.",
          },
          {
            head: "Preparação DELF / DALF / TEF",
            body: "Simulados oficiais, técnicas de prova e revisão final.",
          },
        ],
      },
      {
        icon: "method",
        kicker: "Conteúdos e metodologia",
        text:
          "Explicações claras, atividades interativas, exercícios de fixação, avaliações periódicas e aulas gravadas para você revisar quantas vezes quiser.",
      },
    ],
  },
  en: {
    seoH2: "Content and methodology · French lessons",
    title: "Who I am, what I teach and how.",
    blocks: [
      {
        icon: "who",
        kicker: "Who I am",
        text:
          "Born and raised in Paris, Professor Lionel is a native French teacher running online French lessons and a complete online French course for every level from A1 to C2 — focused on conversation, grammar, vocabulary and DELF, DALF, TEF and TCF exam prep.",
      },
      {
        icon: "plans",
        kicker: "Course plans",
        list: [
          {
            head: "Complete Online Course · A1 to C2",
            body: "A structured track from beginner to advanced, with exclusive materials.",
          },
          {
            head: "Intensive Course · 2× a week",
            body: "Faster progress with personalised feedback every session.",
          },
          {
            head: "DELF / DALF / TEF prep",
            body: "Official mock tests, exam strategy and final review.",
          },
        ],
      },
      {
        icon: "method",
        kicker: "Content and methodology",
        text:
          "Clear explanations, interactive activities, drilling exercises, regular assessments and recorded classes you can rewatch as many times as you want.",
      },
    ],
  },
  fr: {
    seoH2: "Contenus et méthodologie · cours de français",
    title: "Qui je suis, ce que j'enseigne et comment.",
    blocks: [
      {
        icon: "who",
        kicker: "Qui je suis",
        text:
          "Professeur natif de Paris, Lionel propose un cours de français en ligne personnalisé et des cours en ligne de français pour tous les niveaux du A1 au C2, axés sur la conversation, la grammaire, le vocabulaire et la préparation aux examens DELF, DALF, TEF et TCF.",
      },
      {
        icon: "plans",
        kicker: "Plans de cours",
        list: [
          {
            head: "Cours en ligne complet · A1 à C2",
            body: "Parcours structuré du débutant à l'avancé, avec des supports exclusifs.",
          },
          {
            head: "Cours intensif · 2× par semaine",
            body: "Une progression accélérée avec un feedback personnalisé à chaque séance.",
          },
          {
            head: "Préparation DELF / DALF / TEF",
            body: "Simulations officielles, techniques d'examen et révision finale.",
          },
        ],
      },
      {
        icon: "method",
        kicker: "Contenus et méthodologie",
        text:
          "Explications claires, activités interactives, exercices de fixation, évaluations régulières et cours enregistrés à revoir autant de fois que vous voulez.",
      },
    ],
  },
};

const ICONS = {
  who: GraduationCap,
  plans: BookOpen,
  method: Sparkles,
};

const Methodology = () => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;

  return (
    <section
      id="metodologia"
      data-testid="methodology-section"
      className="py-24 sm:py-32 px-6 sm:px-10 lg:px-12 bg-[#f4f1ea] border-t border-[#e5e3db]"
    >
      <span
        id="quem-somos"
        aria-hidden="true"
        className="block -mt-24 pt-24 pointer-events-none"
      />
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <h2 className="overline">{t.seoH2}</h2>
          <p
            data-testid="methodology-title"
            className="font-display mt-4 text-4xl sm:text-5xl tracking-tight text-[#1e2a5a] leading-[1.1]"
          >
            {t.title}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {t.blocks.map((b, i) => {
            const Icon = ICONS[b.icon];
            return (
              <article
                key={i}
                data-testid={`methodology-block-${b.icon}`}
                className="relative bg-white border border-[#e5e3db] rounded-2xl p-7 sm:p-8 transition-all hover:border-[#1e2a5a]/30 hover:shadow-[0_18px_40px_-24px_rgba(30,42,90,0.18)]"
              >
                <span className="absolute top-7 right-7 text-[10px] uppercase tracking-[0.22em] text-[#d9381e] font-medium tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Icon className="h-7 w-7 text-[#1e2a5a]" strokeWidth={1.5} />
                <h3 className="font-display mt-5 text-xl sm:text-2xl text-[#1e2a5a] leading-snug">
                  {b.kicker}
                </h3>
                {b.text && (
                  <p className="mt-3 text-[15px] leading-relaxed text-[#3a3a3a]">
                    {b.text}
                  </p>
                )}
                {b.list && (
                  <ul className="mt-4 space-y-4">
                    {b.list.map((it, j) => (
                      <li key={j} className="border-t border-[#e5e3db] pt-3 first:border-t-0 first:pt-0">
                        <p className="font-semibold text-[14.5px] text-[#1e2a5a] leading-snug">
                          {it.head}
                        </p>
                        <p className="mt-1 text-[13.5px] text-[#5c5c5c] leading-relaxed">
                          {it.body}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Methodology;
